import { FormHeader } from 'components/common';
import {
  useRecognitionApproveQuery,
  useRecognitionConditionalReviewQuery,
  useRecognitionCreateQuery,
  useRecognitionDeleteQuery,
  useRecognitionRejectQuery,
  useRecognitionReopenQuery,
  useRecognitionReviewQuery,
  useRecognitionUpdateQuery,
} from 'hooks';
import { validateHTML } from 'hooks/helpers/common';
import { useModal } from 'hooks/ui/useModal';
import { AllStatuses } from 'interfaces';
import {
  DraftRecognitionValidationField,
  setRecognitionErrors,
  useRecognitionStore,
  validateRecognition,
  validateTitle,
} from 'store/recognition';

export const RecognitionHeader = () => {
  const modal = useModal();
  const { draft } = useRecognitionStore((state) => state);
  const { mutate: sendRecognitionForReview } = useRecognitionReviewQuery();
  const { mutate: sendRecognitionWithCondition } =
    useRecognitionConditionalReviewQuery();
  const { mutate: createRecognition } = useRecognitionCreateQuery({
    keepActive: true,
  });
  const { mutate: updateRecognition } = useRecognitionUpdateQuery();
  const { mutate: deleteRecognition, discardDraft } =
    useRecognitionDeleteQuery();
  const { mutate: approveRecognition } = useRecognitionApproveQuery();
  const { mutate: rejectRecognition } = useRecognitionRejectQuery();
  const { mutate: reopenRecognition } = useRecognitionReopenQuery();

  const validateAllFields = (option: boolean) => {
    const recognitionErrors = validateRecognition(draft, option);
    const hasError = Object.values(recognitionErrors).some((err) => err);

    if (hasError) {
      setRecognitionErrors(recognitionErrors);
    }

    return hasError;
  };

  const handleSaveAsDraft = () => {
    const draftTitle = draft.title.trim();
    const titleError = validateTitle(draftTitle, true);

    if (titleError) {
      return setRecognitionErrors({
        title: titleError,
      } as DraftRecognitionValidationField);
    }

    const hasError = validateAllFields(false);

    if (hasError) return null;

    return modal.confirm({
      text: `Are you sure you want to save "${validateHTML(
        draftTitle,
      )}" as a Draft?`,
      highlights: [`"${validateHTML(draftTitle)}"`, 'save', 'Draft'],
      successAction: 'Yes',
      onSuccess: () => {
        if (draft.id) {
          return updateRecognition(draft);
        }

        return createRecognition(draft);
      },
    });
  };

  const handleSaveForApprove = () => {
    const hasError = validateAllFields(true);

    if (!hasError) {
      modal.confirm({
        text: `Are you sure you want to save "${validateHTML(
          draft.title,
        )}" for Approve?
                    You will not be able to edit it anymore.`,
        highlights: [`"${validateHTML(draft.title)}"`, 'save', 'Approve'],
        successAction: 'Yes',
        onSuccess: () => {
          if (draft.status === AllStatuses.NOT_SAVED) {
            return sendRecognitionWithCondition({
              recognition: draft,
              new: !draft.id,
            });
          }

          return sendRecognitionForReview(draft.id);
        },
      });
    }
  };

  const handleReject = () =>
    modal.confirm({
      text: `Are you sure you want to Reject "${validateHTML(draft.title)}"?
    This action will reopen recognition for editing.`,
      highlights: [`"${validateHTML(draft.title)}"`, 'Reject'],
      successAction: 'Yes',
      onSuccess: () => rejectRecognition(draft.id),
    });

  const handleApprove = () =>
    modal.confirm({
      text: `Are you sure you want to Approve "${validateHTML(draft.title)}"?`,
      highlights: [`"${validateHTML(draft.title)}"`, 'Approve'],
      successAction: 'Yes',
      onSuccess: () => approveRecognition(draft.id),
    });

  const handleSendToRework = () =>
    modal.confirm({
      text: `Are you sure you want to Reopen "${validateHTML(draft.title)}"?`,
      highlights: [`"${validateHTML(draft.title)}"`, 'Reopen'],
      successAction: 'Yes',
      onSuccess: () => reopenRecognition(draft.id),
    });

  const handleDelete = () => {
    if (draft.id) {
      return modal.delete({
        text: `Are you sure you want to Delete ${
          draft.title ? `"${validateHTML(draft.title)}"` : 'this recognition'
        }?`,
        highlights: [
          ` ${
            draft.title ? `"${validateHTML(draft.title)}"` : 'this recognition'
          }`,
          'Delete',
        ],
        onSuccess: () => deleteRecognition(draft.id),
      });
    }

    return modal.delete({
      text: 'Are you sure you want to Delete this draft?',
      highlights: ['Delete', 'draft'],
      onSuccess: () => discardDraft(),
    });
  };

  return (
    <FormHeader
      draft={draft}
      handleApprove={handleApprove}
      handleDelete={handleDelete}
      handleReject={handleReject}
      handleSaveAsDraft={handleSaveAsDraft}
      handleSaveForApprove={handleSaveForApprove}
      handleSendToRework={handleSendToRework}
    />
  );
};
